import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#trackCartUpdate);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#trackCartUpdate);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#verifyOnOpen();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  // Back/forward can show this page from a browser cache with a stale cart, and a drawer opened later can be stale too. Always compare with the real cart.
  #handlePageShow = (event) => void ((event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') && this.#syncWithServerCart());

  // no-store: after Back the browser can answer these requests from its cache with an old cart.
  #fetchCart = () => fetch(Theme.routes.cart_url + '.js', { cache: 'no-store' }).then((response) => response.json());

  #fetchDrawerHtml = () => { const url = new URL(location.href); url.searchParams.set('section_id', this.#sectionId); return fetch(url, { cache: 'no-store' }).then((response) => response.text()); };

  // Re-read the real cart, then refresh the drawer content and the header count so both match it.
  #syncWithServerCart = () => Promise.all([this.#fetchCart(), this.#fetchDrawerHtml()]).then(([cart, html]) => morphSection(this.#sectionId, html, { mode: 'hydration', injectStylesheet: true }).then(() => this.#showCartCount(cart.item_count))).catch((error) => console.warn('[cart-drawer] Could not refresh the cart:', error));

  #showCartCount = (count) => { document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(count, false)); requestAnimationFrame(() => this.#updateStickyState()); };

  #lastCartUpdateAt = 0;

  // A drawer that was not opened by a cart update is checked against the real cart when it opens.
  #trackCartUpdate = () => { this.#lastCartUpdateAt = Date.now(); };

  #drawerMatches = (cart) => [...this.querySelectorAll('cart-items-component [data-key]')].map((row) => row.dataset.key).sort().join() === cart.items.map((item) => item.key).sort().join() && (this.querySelector('.cart-drawer--empty') ? 0 : Number(this.querySelector('.theme-drawer__badge')?.textContent)) === cart.item_count;

  #verifyOnOpen = () => Date.now() - this.#lastCartUpdateAt < 3000 || this.#fetchCart().then((cart) => (this.#drawerMatches(cart) ? this.#showCartCount(cart.item_count) : this.#syncWithServerCart())).catch(() => {});

  get #sectionId() { return this.querySelector('cart-items-component')?.dataset.sectionId ?? 'cart-drawer-section'; }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
